import { message } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import userLocationRetrieve from "./UserLocationRetrieve";
import ReverseGeocoding from "./ReverseGeoCoding";
import { bookingStatusDetails } from "./constants";
import {
  setChannelsUnreadMsgs,
  setChannelUnreadMsgsStatus,
  setChatChannels,
} from "../../store/common/chatSlice";
import { store } from "../../store";
import { FaCircleCheck } from "react-icons/fa6";
import {
  setError,
  setIsPushNotificationSupported,
} from "../../store/common/generalSlice";
import { generateFCMToken } from "../firebase/firebase";
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

let snackBarRef = null;

const saveSnackbarRef = (ref) => {
  snackBarRef = ref;
};

const showSnackBar = ({ message, vertical, horizontal, severity, style }) => {
  snackBarRef?.setState({
    open: true,
    message: message || "success",
    vertical: vertical || "top",
    horizontal: horizontal || "center",
    severity: severity || "success",
    style,
  });
};

const showMessage = (status, content) => {
  if (status == "success") {
    message.success(content);
  }
  if (status == "warning") {
    message.warning(content);
  }
  if (status == "error") {
    message.error(content);
  }
};

function camelCaseToTitle(text) {
  var result = text.replace(/([A-Z])/g, " $1");
  var finalResult = result.charAt(0).toUpperCase() + result.slice(1);
  return finalResult;
}

const numWithCommas = (num) =>
  num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
const AccountingFormat = (amount, currency = "") => {
  if (amount < 0)
    return `${currency}(${numWithCommas(
      Number(Math.abs(amount)).toFixed(2).replace("-", ""),
    )})`;
  else
    return `${currency}${numWithCommas(
      Number(amount).toFixed(2).replace("-", ""),
    )}`;
};

const withRouter = (Component) => {
  const Wrapper = (props) => {
    const navigate = useNavigate();

    return <Component navigate={navigate} {...props} />;
  };

  return Wrapper;
};
const emailRegex =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,3}))$/;

const getOSName = () => {
  const userAgent = window.navigator.userAgent;
  let osName = "Unknown";

  if (userAgent.indexOf("Win") !== -1) osName = "Windows";
  if (userAgent.indexOf("Mac") !== -1) osName = "MacOS";
  if (userAgent.indexOf("X11") !== -1) osName = "UNIX";
  if (userAgent.indexOf("Linux") !== -1) osName = "Linux";
  if (/Android/.test(userAgent)) osName = "Android";
  if (/iPhone|iPad|iPod/.test(userAgent)) osName = "iOS";

  return osName;
};

const formattedDateTime = (dateTime) => {
  if (!dateTime) return;

  const dateStr = dateTime;
  const date = new Date(dateStr);

  // Format the date and time components
  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "long" });
  const hours = date.getHours() % 12 || 12; // Convert to 12-hour format
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = date.getHours() >= 12 ? "pm" : "am";

  // Construct the final formatted string
  const finalFormattedDate = `${day} ${month} at ${hours}:${minutes} ${ampm}`;

  return finalFormattedDate;
};

const compareWithCurrentDateTime = (dateString) => {
  // const givenDate = new Date(dateString);
  // const currentDate = new Date();

  let givenDate;
  //has both date and time
  if (dateString && dateString.includes("T")) {
    // Extract date and time parts
    const [date, time] = dateString.split("T");
    const formattedTime = time.split(".")[0].slice(0, 5);
    givenDate = `${date} ${formattedTime}`;
  } else {
    givenDate = dayjs.utc(dateString).local().format("DD-MM-YYYY HH:mm");
  }

  const currentDate = dayjs().format("DD-MM-YYYY HH:mm");

  if (givenDate > currentDate) {
    return true;
  } else if (givenDate < currentDate) {
    return false;
  } else {
    return false;
  }
};

const checkIfFilesAreTooBig = (file, size = 5000000) => {
  if (!file) return;

  let valid = true;
  if (file.size > size) {
    valid = false;
  }
  return valid;
};

const checkIfFilesAreCorrectType = (file) => {
  if (!file) return;

  let valid = true;
  if (!["image/jpg", "image/jpeg", "image/png"].includes(file.type)) {
    valid = false;
  }
  return valid;
};

//convert yup object to file
function convertToFile(yupObject) {
  return new File([yupObject], yupObject.name, {
    type: yupObject.type,
    lastModified: yupObject.lastModified,
  });
}

// Function to get dimensions from a file
const getDimensionsFromFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function (event) {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        // Return the result based on the image dimensions
        resolve(img.width > 800 || img.height > 600 ? false : true);
      };

      img.onerror = () => {
        reject(false); // Reject the promise on error
      };
    };

    reader.onerror = () => {
      reject(false); // Reject if FileReader fails
    };
  });
};

// Function to get dimensions from an image URL
const getDimensionsFromImageUrl = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = url;

    img.onload = () => {
      // Return the result based on the image dimensions
      resolve(img.width > 800 || img.height > 600 ? false : true);
    };

    img.onerror = () => {
      reject(false); // Reject the promise on error
    };
  });
};

// Function to check dimensions, which handles files and URLs
const checkDimensions = async (data) => {
  if (!data) return;

  let isValidImg;
  try {
    if (data instanceof File) {
      isValidImg = await getDimensionsFromFile(data); // Await the promise
    } else if (data instanceof Blob) {
      // Handle Blob if needed
    } else {
      isValidImg = await getDimensionsFromImageUrl(data); // Await the promise
    }
    return isValidImg;
  } catch (error) {
    return false;
  }
};

const getCurrentLocation = async () => {
  const coords = await userLocationRetrieve();
  if (coords && coords?.latitude) {
    const location = await ReverseGeocoding({
      lat: coords?.latitude,
      lng: coords?.longitude,
    });
    return {
      lat: coords?.latitude,
      lng: coords?.longitude,
      address: location?.formatted_address,
      postalCode: location?.postalCode,
      town: location?.town,
      city: location?.city,
      country: location?.country,
    };
  }
};

function getCurrentMonthName() {
  const today = new Date();
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const currentMonth = today.getMonth(); // Returns 0 for January, 11 for December
  return monthNames[currentMonth];
}

const getDistanceByCoordinates = (location1, location2) => {
  function haversineDistance(coords1, coords2) {
    const toRadians = (angle) => (angle * Math.PI) / 180;

    const { latitude: lat1, longitude: lon1 } = coords1;
    const { latitude: lat2, longitude: lon2 } = coords2;

    const R = 3958.8; // Radius of the Earth in miles
    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRadians(lat1)) *
        Math.cos(toRadians(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in miles
  }

  const x = { latitude: location1?.latitude, longitude: location1?.longitude };
  const y = { latitude: location2?.latitude, longitude: location2?.longitude };

  const distance = haversineDistance(x, y);
  return distance.toFixed(2);
};

const getServicesList = (services, showMoreDots = false) => {
  return (
    <ul className="m-0 p-0">
      {services.map((service, index) => (
        <li key={index}>
          {index + 1}. {service?.name}
        </li>
      ))}
      {showMoreDots && "..."}
    </ul>
  );
};

// creating that nested three layers of services to display.
const getAllServices = (selectedCategories) => {
  if (selectedCategories?.length > 0) {
    return (
      <div className="mb-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 w-full">
        {selectedCategories.map((firstLayer) => {
          return (
            <div className="flex flex-col justify-start gap-2 mb-4">
              <div className="flex items-start gap-2">
                <small className="text-base font-semibold text-[#A0A0AD]">
                  {firstLayer?.name}
                </small>
              </div>
              {firstLayer?.subServices?.length > 0 &&
                firstLayer?.subServices.map((secondLayer) => {
                  return (
                    <>
                      <div className="flex items-start gap-2 pl-2">
                        <small className="font-md text-sm text-[#FAB700]">
                          <span className="text-lg">.</span> {secondLayer?.name}
                        </small>
                      </div>
                      {secondLayer?.subServices?.length > 0 &&
                        secondLayer?.subServices.map((thirdLayer) => {
                          return (
                            <>
                              <div className="flex items-start gap-2 pl-2">
                                <div className="w-[20px]" key={thirdLayer?.id}>
                                  <FaCircleCheck className="text-md text-[#FAB700] bg-white rounded-full mr-2" />
                                </div>
                                <small className="font-md text-xs">
                                  {thirdLayer?.name?.length > 13
                                    ? thirdLayer?.name.slice(0, 14) + "..."
                                    : thirdLayer?.name}
                                </small>
                              </div>
                            </>
                          );
                        })}
                    </>
                  );
                })}
            </div>
          );
        })}
      </div>
    );
  } else {
    return <p>No service available</p>;
  }
};

// creating that last layer of services to display.
const getLastLayerServices = (services) => {
  return (
    <div className="mb-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 w-full">
      {services.map((service, index) => (
        <p key={index}>
          {index + 1}. {service?.name}
          {/* <span className="font-bold">{" £ "+service?.percentage+"%"}</span> */}
        </p>
      ))}
    </div>
  );
};

const getBookingStatus = (statusIdx) => {
  if (statusIdx > -1)
    return bookingStatusDetails[statusIdx]?.label.toLowerCase();
};

const getFormattedDateTime = (dateTimeStr, format = "DD-MM-YYYY HH:mm") => {
  return dayjs(dateTimeStr).format(format);
};

const getEllipsedString = (str, reqStrLen) => {
  if (!str || typeof str != "string") return "";

  return (str.length && str.length) > reqStrLen
    ? str.slice(0, reqStrLen - 1) + "..."
    : str;
};

const getInitials = (name) => {
  if (!name || typeof name != "string") return "";

  const words = name.trim().split(/\s+/);
  return words
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
};

const notifyWhenChannelHasUnreadMessages = (
  allChannels,
  unreadMsgChannels,
  userId,
) => {
  if (!allChannels || !unreadMsgChannels || !userId) return false;

  if (allChannels.length) {
    const remChannels = allChannels.filter(
      (e) =>
        !unreadMsgChannels.map((item) => item.channelId).includes(e.channelId),
    );
    const all = [
      ...(unreadMsgChannels.length ? unreadMsgChannels : []),
      ...(remChannels.length ? remChannels : []),
    ];

    let total = 0;
    for (let i = 0; i < all.length; i++) {
      if (all[i].unreadMessageCount === undefined) {
        const myUnreadMsgs = all[i]?.chatMembers.filter(
          (e) => e.memberId == userId,
        );
        if (myUnreadMsgs.length) {
          total += myUnreadMsgs[0]?.unreadMessageCount;
        }
      } else {
        total += all[i]?.unreadMessageCount;
      }
    }
    store.dispatch(setChannelUnreadMsgsStatus(total ? true : false));
    // localStorage.setItem('hasUnreadMsgs', JSON.stringify(total ? true : false))
  }
};

const setChannelUnreadMsgsCount = (notificationData) => {
  const { channelId, unreadMessageCount, message } = notificationData;

  const updatedAt = dayjs().utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
  const obj = { channelId, unreadMessageCount, message, updatedAt };

  const { channelsUnreadMsgs, chatChannels } = store.getState().chats;
  let tmpChannelsUnreadMsgs;
  if (channelsUnreadMsgs) {
    if (channelsUnreadMsgs.length) {
      tmpChannelsUnreadMsgs = JSON.parse(JSON.stringify(channelsUnreadMsgs));

      const channelIdx = channelsUnreadMsgs.findIndex(
        (e) => e.channelId == channelId,
      );
      if (channelIdx > -1) {
        tmpChannelsUnreadMsgs[channelIdx] = obj;
      } else {
        tmpChannelsUnreadMsgs.push(obj);
      }
    } else {
      tmpChannelsUnreadMsgs = [];
      tmpChannelsUnreadMsgs.push(obj);
    }
  } else return false;

  let sortedMsgsByDateTime = tmpChannelsUnreadMsgs;
  if (tmpChannelsUnreadMsgs.length > 1) {
    sortedMsgsByDateTime = tmpChannelsUnreadMsgs.sort((a, b) =>
      dayjs(b.updatedAt).diff(dayjs(a.updatedAt)),
    );
  }

  localStorage.setItem(
    "channelsUnreadMsgCount",
    JSON.stringify(sortedMsgsByDateTime),
  );
  store.dispatch(setChannelsUnreadMsgs(sortedMsgsByDateTime));

  setTimeout(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    notifyWhenChannelHasUnreadMessages(
      chatChannels,
      sortedMsgsByDateTime,
      user?.id,
    );
  }, 2000);
};

const getValidUrl = (baseUrl) => {
  if (!baseUrl) return null;

  if (!baseUrl.startsWith("http://") && !baseUrl.startsWith("https://")) {
    return "https://" + baseUrl; // Default to HTTPS for security
  }
};

const resetSelectedChannelUnreadMsgCount = (channelId) => {
  const { channelsUnreadMsgs, chatChannels } = store.getState().chats;
  const user = JSON.parse(localStorage.getItem("user"));
  const updatedAt = dayjs().utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
  const obj = { channelId, unreadMessageCount: 0, updatedAt };

  let updatedUnreadMsgs = [];
  if (channelsUnreadMsgs && channelsUnreadMsgs.length) {
    const selectedChannelIdx = channelsUnreadMsgs.findIndex(
      (e) => e.channelId == channelId,
    );
    if (selectedChannelIdx > -1) {
      updatedUnreadMsgs = channelsUnreadMsgs.map((item, index) =>
        index === selectedChannelIdx ? obj : item,
      );
    } else {
      updatedUnreadMsgs = [...channelsUnreadMsgs, obj];
    }
  } else {
    updatedUnreadMsgs = [obj];
  }

  localStorage.setItem(
    "channelsUnreadMsgCount",
    JSON.stringify(updatedUnreadMsgs),
  );
  store.dispatch(setChannelsUnreadMsgs(updatedUnreadMsgs));

  let updatedChatChannels = chatChannels;
  if (chatChannels && chatChannels.length) {
    updatedChatChannels = chatChannels.map((channel) => {
      if (channel.id == channelId) {
        const updatedChatMembers = (channel.chatMembers || []).map((member) => {
          if (member.memberId == user?.id) {
            return { ...member, unreadMessageCount: 0 };
          }
          return member;
        });
        return { ...channel, chatMembers: updatedChatMembers };
      }
      return channel;
    });
    store.dispatch(setChatChannels(updatedChatChannels));
  }

  notifyWhenChannelHasUnreadMessages(
    updatedChatChannels || [],
    updatedUnreadMsgs,
    user?.id,
  );
};

const filterSpecialCharacters = (input) => {
  return input.replace(/[^a-zA-Z0-9 ]/g, "");
};

const capitalizeString = (str) => {
  if (!str || !str.length) return "";

  return str.slice(0, 1).toUpperCase() + str.slice(1);
};

const getAllServicesIds = (services) => {
  if (services?.length > 0) {
    let serviceIds = [];
    services.forEach((parent) => {
      serviceIds.push(parent?.id || 0);
      if (parent?.subServices?.length > 0) {
        parent?.subServices.forEach((subParent) => {
          serviceIds.push(subParent?.id || 0);
          if (subParent?.subServices?.length > 0) {
            subParent?.subServices.forEach((subSubParent) => {
              serviceIds.push(subSubParent?.id || 0);
            });
          }
        });
      }
    });
    return serviceIds;
  } else {
    return [];
  }
};

const handleAdsExternalRedirect = () => {
  const userAgent = navigator.userAgent || window.opera;

  const isIPhone = /iPhone/i.test(userAgent);
  const isIPod = /iPod/i.test(userAgent);
  const isIPad = /iPad/i.test(userAgent);

  // iPadOS 13+ reports as Macintosh, but has touch
  const isModernIPad =
    /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1;

  const isIOS = isIPhone || isIPod || isIPad || isModernIPad;

  const isMac = /Macintosh/i.test(userAgent) && !isModernIPad;

  const playStoreUrl =
    "https://play.google.com/store/apps/details?id=com.MobrageLtd.mobrage.stage&hl=en&pli=1";
  const appStoreUrl = "https://apps.apple.com/gb/app/mobrage/id1644131291";

  if (isIOS || isMac || isIPad) {
    window.open(appStoreUrl, "_blank");
  } else {
    window.open(playStoreUrl, "_blank");
  }
};

const isUnsupportedBrowser = () => {
  const ua = navigator.userAgent;

  // Step 1: Feature detection
  const fcmSupported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  if (!fcmSupported) return { unsupported: true, reason: "core-features" };

  // Step 2: UA-based refinement
  const isSafari = /Safari/.test(ua) && !/Chrome|Chromium|Android/.test(ua);
  const isFirefox = /Firefox/.test(ua);
  const isBrave = /Brave/.test(ua);
  const isAndroidWebView =
    /\bwv\b/.test(ua) ||
    (/Version\/[\d.]+ Chrome/.test(ua) && /Android/.test(ua));

  if (isSafari) {
    const safariMatch = ua.match(/Version\/(\d+)\.(\d+)/);
    const safariMajor = safariMatch ? parseInt(safariMatch[1], 10) : 0;
    const isSupportedSafari = safariMajor >= 17;
    return { unsupported: !isSupportedSafari, reason: "safari-version" };
  }

  if (isFirefox) return { unsupported: true, reason: "firefox" };
  if (isAndroidWebView) return { unsupported: true, reason: "android-webview" };
  if (isBrave) return { unsupported: true, reason: "brave" };

  return { unsupported: false };
};

const getFcmToken = async (swRegistration = null) => {
  const { unsupported } = isUnsupportedBrowser();

  if (unsupported) {
    store?.dispatch(setIsPushNotificationSupported(false));
    return null;
  } else {
    store?.dispatch(setIsPushNotificationSupported(true));

    if (Notification?.permission !== "granted") {
      store?.dispatch(setError("Please enable browser notifications."));
      return false;
    }
  }

  let existingToken = localStorage.getItem("fcmToken");
  if (!existingToken) {
    const newToken = await generateFCMToken(swRegistration);
    if (newToken) {
      existingToken = localStorage.getItem("fcmToken");
    }
  }
  return existingToken || null;
};

const generateFallbackDeviceToken = () => {
  // Simple UUID v4 generator
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

//   [
//     {
//       name: ' Car Wash',
//       firstParentId: 16,
//       subServices: [
//         {
//           name: 'Half Wash',
//           secondParentId: 21,
//           subServices: [ { id: 23, name: 'Cold wash' }, { id: 22, name: 'Warm Wash' } ]
//         },
//         {
//           name: 'Full wash',
//           secondParentId: 17,
//           subServices: [
//             { id: 20, name: 'Partial wash' },
//             { id: 19, name: 'Inner wash' },
//             { id: 18, name: 'Hand wash' }
//           ]
//         }
//       ]
//     }
//   ]

// [
//     {
//       id: 3848,
//       service: { id: 16, parentId: null, name: ' Car Wash' },
//       userServicePrice: null
//     },
//     {
//       id: 3849,
//       service: { id: 21, parentId: 16, name: 'Half Wash' },
//       userServicePrice: null
//     },
//   ]

// const updateSingleThirdLayerSelection = (
//   currentStructure,
//   service,
//   subService,
//   thirdLayerId,
//   checked,
//   selectAll = false
// ) => {
//   const firstLayerId = service.firstParentId;
//   const secondLayerId = subService.secondParentId;
//   const action = checked ? 'add' : 'remove';

//   console.log(firstLayerId)
//   console.log(secondLayerId)

//   // Deep clone the structure safely
//   let cloneStructure = currentStructure
//     ? JSON.parse(JSON.stringify(currentStructure))
//     : {
//       firstLayerId,
//       selections: []
//     };

//   // 🔒 DO NOT reinitialize if already exists — preserve structure!
//   if (!cloneStructure.selections) {
//     cloneStructure.selections = [];
//   }

//   // Find existing second layer
//   let secondLayerEntry = cloneStructure.selections.find(
//     s => s.secondLayerId === secondLayerId
//   );

//   if (selectAll) {
//     // ✅ SELECT ALL CASE
//     const allThirdLayerServices = subService.subServices.map(item => ({
//       thirdLayerId: item.id,
//       action
//     }));

//     if (secondLayerEntry) {
//       // Merge or replace (here: overwrite cleanly)
//       secondLayerEntry.action = action;

//       // Avoid duplication
//       const existingIds = new Set(
//         secondLayerEntry.services.map(s => s.thirdLayerId)
//       );

//       allThirdLayerServices.forEach(service => {
//         if (!existingIds.has(service.thirdLayerId)) {
//           secondLayerEntry.services.push(service);
//         }
//       });

//     } else {
//       // Add new second layer entry
//       cloneStructure.selections.push({
//         secondLayerId,
//         action,
//         services: allThirdLayerServices
//       });
//     }

//   } else {
//     // ✅ SINGLE THIRD-LAYER UPDATE
//     if (!secondLayerEntry) {
//       // Add new second layer
//       secondLayerEntry = {
//         secondLayerId,
//         action: null,
//         services: []
//       };
//       cloneStructure.selections.push(secondLayerEntry);
//     }

//     if (checked) {
//       const existing = secondLayerEntry.services.find(
//         s => s.thirdLayerId === thirdLayerId
//       );

//       if (existing) {
//         existing.action = action;
//       } else {
//         secondLayerEntry.services.push({ thirdLayerId, action });
//       }
//     } else {
//       // Remove if unchecked
//       secondLayerEntry.services = secondLayerEntry.services.filter(
//         s => s.thirdLayerId !== thirdLayerId
//       );
//     }

//     // Cleanup: remove secondLayer if no services left
//     if (secondLayerEntry.services.length === 0) {
//       cloneStructure.selections = cloneStructure.selections.filter(
//         s => s.secondLayerId !== secondLayerId
//       );
//     } else {
//       // Keep secondLayer, but mark action as null (not selectAll)
//       secondLayerEntry.action = null;
//     }
//   }
//   console.log(cloneStructure)
//   return cloneStructure;
// };

// const getStructuredServices = (services, editData) => {
//   const structureData = {};
//   if (services?.length > 0) {
//     for (let service of services) {
//       structureData.firstParentId = service?.firstParentId;
//       for (let subService of service?.subServices) {
//         const secondItem = editData?.find(editService => editService?.service?.id === subService?.secondParentId);
//         // if item exists in edit data then only we have to add it.
//         if (secondItem !== undefined) {
//           const structureSecondItemIndex = structureData?.services?.findIndex(structureService => structureService?.secondParentId === secondItem?.service?.id);
//           if (structureSecondItemIndex !== -1) {
//             // means already exists.
//             // structureData.services[structureSecondItemIndex] = {
//             //   secondParentId: subService?.secondParentId,
//             //   thirdParentId: [...structureData?.services[structureSecondItemIndex]?.thirdParentId]
//             // }
//             continue;
//           } else {
//             structureData = {
//               ...structureData,
//               services: [
//                 ...structureData?.services,
//                 {
//                   secondParentId: secondItem?.service?.id,
//                   thirdParentId: []
//                 }
//               ]
//             }
//           }
//         }
//         else {
//           for (let subSubService of subService?.subServices) {
//             const thirdItem = editData?.find(editService => editService?.service?.id === subSubService?.id);
//             // if item exists in edit data then only we have to add it.
//             if (thirdItem !== undefined) {
//               const structureSecondItemIndex = structureData?.services?.findIndex(structureService => structureService?.secondParentId === secondItem?.service?.id);
//               if (structureSecondItemIndex !== -1) {
//                 // means already exists.
//                 structureData.services[structureSecondItemIndex] = {
//                   secondParentId: subService?.secondParentId,
//                   thirdParentId: [...structureData?.services[structureSecondItemIndex]?.thirdParentId, thirdItem?.id]
//                 }
//               }
//               else {
//                 structureData = {
//                   ...structureData,
//                   services: [
//                     ...structureData?.services,
//                     {
//                       secondParentId: secondItem?.service?.id,
//                       thirdParentId: [thirdItem?.id]
//                     }
//                   ]
//                 }
//               }

//             }
//           }
//         }
//       }
//     }
//   }
//   console.log(structureData)
// }

const getStructuredServices = (services, editData) => {
  const structureData = {
    firstParentId: null,
    services: [],
  };

  if (services?.length > 0) {
    for (let service of services) {
      structureData.firstParentId = service?.firstParentId;

      for (let subService of service?.subServices || []) {
        const secondItem = editData?.find(
          (editService) =>
            editService?.service?.id === subService?.secondParentId,
        );

        const secondParentId = subService?.secondParentId;

        // If second level exists in editData
        if (secondItem !== undefined) {
          const exists = structureData.services.some(
            (s) => s.secondParentId === secondParentId,
          );
          if (!exists) {
            structureData.services.push({
              secondParentId,
              thirdParentId: [],
            });
          }
        }

        // else {
        // Check third layer
        for (let subSubService of subService?.subServices || []) {
          const thirdItem = editData?.find(
            (editService) => editService?.service?.id === subSubService?.id,
          );

          if (thirdItem) {
            // Check if this secondParentId exists already
            let existing = structureData.services.find(
              (s) => s.secondParentId === secondParentId,
            );

            if (existing) {
              // Avoid duplicates
              if (!existing.thirdParentId.includes(subSubService?.id)) {
                existing.thirdParentId.push(subSubService?.id);
              }
            } else {
              structureData.services.push({
                secondParentId,
                thirdParentId: [subSubService?.id],
              });
            }
          }
        }
        // }
      }
    }
  }

  console.log(structureData);
  return structureData;
};

export {
  snackBarRef,
  withRouter,
  saveSnackbarRef,
  showSnackBar,
  emailRegex,
  camelCaseToTitle,
  AccountingFormat,
  showMessage,
  getOSName,
  formattedDateTime,
  compareWithCurrentDateTime,
  checkIfFilesAreTooBig,
  checkIfFilesAreCorrectType,
  checkDimensions,
  convertToFile,
  getCurrentLocation,
  getCurrentMonthName,
  getDistanceByCoordinates,
  getServicesList,
  getBookingStatus,
  getFormattedDateTime,
  getEllipsedString,
  getInitials,
  setChannelUnreadMsgsCount,
  notifyWhenChannelHasUnreadMessages,
  getValidUrl,
  resetSelectedChannelUnreadMsgCount,
  capitalizeString,
  filterSpecialCharacters,
  getAllServices,
  getAllServicesIds,
  handleAdsExternalRedirect,
  getFcmToken,
  generateFallbackDeviceToken,
  getStructuredServices,
  getLastLayerServices,
};
